import { useEffect } from 'react';
import { useIsRecording } from '@livekit/components-react';
import { getStore } from '../../../stores/StoreContext';

/**
 * RecordingMonitor - Monitors LiveKit recording status and updates ExamSessionStore
 * 
 * This component isolates the useIsRecording() hook to prevent unnecessary re-renders
 * in parent components. LiveKit's useIsRecording() hook triggers re-renders when
 * room state changes (including audio activity), even if the recording status hasn't
 * changed. By isolating it here, only this component re-renders on audio activity.
 * 
 * **Performance Impact:**
 * - Prevents parent Layout component from re-rendering on every audio activity change
 * - Only updates ExamSessionStore when recording status actually changes
 * - No UI rendering overhead (returns null)
 * 
 * **Usage:**
 * Place this component inside LiveKitRoom alongside other LiveKit components.
 * It will automatically monitor and sync recording status to the store.
 * 
 * @component
 * @returns {null} No UI - monitoring only
 * 
 * @example
 * <LiveKitRoom token={token} serverUrl={url}>
 *   <RecordingMonitor />
 *   <SessionLayout />
 * </LiveKitRoom>
 */
function RecordingMonitor() {
    const ExamSessionStore = getStore('examSessionStore');
    const isRecording = useIsRecording();

    // Update store when recording status changes
    useEffect(() => {
        ExamSessionStore.setIsMediaServerRecording(isRecording);
    }, [isRecording, ExamSessionStore]);

    // No UI - this is a monitoring component only
    return null;
}

export default RecordingMonitor;
